import React from "react";
import { useAuth } from "@/contexts/AuthContext";

interface CurrencyIconProps {
  className?: string;
  size?: number;
  /** Optional override glyph; defaults to the farmer's countries.currency_symbol. */
  symbol?: string;
}

/**
 * Renders the farmer's currency glyph (countries.currency_symbol), single-sourced
 * from the AuthContext user (resolved from profiles.country_id). No hardcoded
 * country→currency map, no BDT default, no per-currency image icons.
 *
 * When no currency has resolved yet, renders a neutral, correctly-sized blank
 * rather than a wrong-currency symbol.
 */
export const CurrencyIcon: React.FC<CurrencyIconProps> = ({
  className = "",
  size = 24,
  symbol,
}) => {
  const { user } = useAuth();

  const glyph = symbol ?? user?.currencySymbol ?? null;
  const currencyCode = user?.currencyCode ?? null;

  return (
    <span
      className={className}
      style={{
        fontSize: size * 0.85,
        fontWeight: 700,
        lineHeight: 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        // Khmer riel needs a Khmer-capable font to render reliably.
        ...(currencyCode === "KHR"
          ? { fontFamily: '"Noto Sans Khmer", "Khmer OS", sans-serif' }
          : {}),
      }}
      aria-label={currencyCode || undefined}
    >
      {glyph}
    </span>
  );
};
